import React, { useEffect } from "react";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Download,
  FileText,
  HelpCircle,
  Lock,
  Mail,
  MessageCircleQuestion,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Video,
  BookOpen,
  ClipboardCheck,
  Smartphone,
  Send,
} from "lucide-react";

type SeoProps = {
  path: string;
  title: string;
  description: string;
  keywords: string;
};

type InfoSection = {
  title: string;
  description: string;
  points?: string[];
};

type FaqItem = {
  question: string;
  answer: string;
};

type SupportCard = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const contactInfo = {
  phone: "+919412220200",
  displayPhone: "+91 94122 20200",
  email: "support@mediitacademy.com",
};

const usePageSeo = ({ path, title, description, keywords }: SeoProps) => {
  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    canonical.setAttribute("href", `${origin}${path}`);
  }, [path, title, description, keywords]);
};

const JsonLd: React.FC<{ data: object }> = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify(data),
    }}
  />
);

const SupportStrip: React.FC = () => {
  return (
    <section className="bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-8">
          <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-600">
            Need Help?
          </p>

          <h2 className="mt-3 text-3xl font-black text-[#071f54]">
            Mediit Academy Support Team Is Here to Help
          </h2>

          <p className="mt-4 text-sm font-medium leading-7 text-slate-600">
            For course selection, live class access, payment confirmation,
            refund request, doubt support, student login issues, app updates or
            academic guidance, connect with the official support team.
          </p>
        </div>

        <div className="space-y-3 lg:col-span-4">
          <a
            href={`tel:${contactInfo.phone}`}
            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-black text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
          >
            <Phone size={18} aria-hidden="true" />
            {contactInfo.displayPhone}
          </a>

          <a
            href={`mailto:${contactInfo.email}`}
            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-black text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
          >
            <Mail size={18} aria-hidden="true" />
            {contactInfo.email}
          </a>
        </div>
      </div>
    </section>
  );
};

const LegalPage: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  path: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  icon: React.ReactNode;
  sections: InfoSection[];
  faqs?: FaqItem[];
}> = ({
  eyebrow,
  title,
  description,
  path,
  seoTitle,
  seoDescription,
  seoKeywords,
  icon,
  sections,
  faqs = [],
}) => {
  usePageSeo({
    path,
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seoTitle,
    description: seoDescription,
    url: path,
    publisher: {
      "@type": "EducationalOrganization",
      name: "Mediit Academy",
      email: contactInfo.email,
      telephone: contactInfo.displayPhone,
    },
    keywords: seoKeywords,
  };

  const faqSchema =
    faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd data={pageSchema} />
      {faqSchema && <JsonLd data={faqSchema} />}

      {/* Simple policy hero - gap fixed */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-blue-700 shadow-sm ring-1 ring-slate-200">
                {icon}
              </div>

              <p className="mt-6 text-sm font-black uppercase tracking-[0.18em] text-blue-700">
                {eyebrow}
              </p>

              <h1 className="mt-3 text-4xl font-black leading-tight tracking-[-0.04em] text-[#071f54] sm:text-5xl">
                {title}
              </h1>

              <p className="mt-5 max-w-3xl text-base font-medium leading-8 text-slate-600">
                {description}
              </p>

              <p className="sr-only">{seoKeywords}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:max-w-sm">
              <p className="text-sm font-black text-[#071f54]">
                Official Information
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                This page explains important policy details for students,
                parents, visitors and enrolled learners of Mediit Academy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal content */}
      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">
                On This Page
              </p>

              <div className="mt-5 space-y-3">
                {sections.map((section, index) => (
                  <a
                    key={section.title}
                    href={`#section-${index + 1}`}
                    className="block rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:text-blue-700"
                  >
                    {String(index + 1).padStart(2, "0")} · {section.title}
                  </a>
                ))}
              </div>
            </div>
          </aside>

          <div className="space-y-6 lg:col-span-8">
            {sections.map((section, index) => (
              <article
                id={`section-${index + 1}`}
                key={section.title}
                className="scroll-mt-28 rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
              >
                <div className="mb-5 flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-black text-blue-700">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <div>
                    <h2 className="text-2xl font-black text-[#071f54]">
                      {section.title}
                    </h2>

                    <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                      {section.description}
                    </p>
                  </div>
                </div>

                {section.points && section.points.length > 0 && (
                  <ul className="mt-5 space-y-3 border-t border-slate-100 pt-5">
                    {section.points.map((point) => (
                      <li
                        key={point}
                        className="flex gap-3 text-sm leading-6 text-slate-600"
                      >
                        <CheckCircle2
                          size={18}
                          className="mt-0.5 shrink-0 text-blue-700"
                          aria-hidden="true"
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="mb-8 text-center">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">
                Policy FAQs
              </p>

              <h2 className="mt-3 text-3xl font-black text-[#071f54]">
                Common Questions
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <article
                  key={faq.question}
                  className="rounded-[1.25rem] border border-slate-200 bg-white p-6"
                >
                  <h3 className="text-lg font-black text-[#071f54]">
                    {faq.question}
                  </h3>

                  <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                    {faq.answer}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <SupportStrip />
    </main>
  );
};

export const HelpCenter: React.FC = () => {
  const seoTitle =
    "Help Center | Student Support, Login Help, Course Access - Mediit Academy";
  const seoDescription =
    "Mediit Academy Help Center for student login, course access, live classes, notes, test series, payments, refunds, technical support and academic guidance.";
  const seoKeywords =
    "Mediit Academy help center, student support, online course help, login help, live class support, course access help, test series help, payment support, study notes support, academic guidance";

  usePageSeo({
    path: "/help-center",
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  const supportCards: SupportCard[] = [
    {
      title: "Login & Account",
      description:
        "Get help with student login, OTP verification, registration, profile update and dashboard access.",
      icon: <Lock size={24} />,
    },
    {
      title: "Course Access",
      description:
        "Understand enrolled courses, live class links, notes, assignments, PDFs and test series access.",
      icon: <BookOpen size={24} />,
    },
    {
      title: "Payments & Refunds",
      description:
        "Get support for payment verification, invoice request, failed transaction and refund guidance.",
      icon: <FileText size={24} />,
    },
    {
      title: "Technical Support",
      description:
        "Resolve dashboard loading, video access, browser, device and learning platform issues.",
      icon: <HelpCircle size={24} />,
    },
  ];

  const faqs: FaqItem[] = [
    {
      question: "Why is my enrolled course not showing?",
      answer:
        "It may happen due to pending payment verification, incorrect login details or batch activation delay. Contact support with your registered mobile number and payment proof.",
    },
    {
      question: "Where can I find live class links?",
      answer:
        "Live class links are usually available in the student dashboard, enrolled course page or batch schedule section after course activation.",
    },
    {
      question: "Can I get help for test series access?",
      answer:
        "Yes, support can help with test series access, test visibility, score reports and technical issues during online tests.",
    },
  ];

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: seoTitle,
          description: seoDescription,
          url: "/help-center",
          keywords: seoKeywords,
        }}
      />

      {/* Different layout - Help Center */}
      <section className="bg-[#07111b] px-4 py-12 text-white sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-cyan-300">
              Help Center
            </p>

            <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              Student Support for Courses, Login, Live Classes and Payments
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300">
              Find quick help for student login, course access, live class
              links, notes, test series, payment confirmation, refund requests,
              technical support and academic guidance.
            </p>

            <div className="mt-7 flex max-w-xl items-center gap-3 rounded-2xl border border-white/10 bg-white/10 p-3">
              <Search size={20} className="text-cyan-300" />
              <p className="text-sm text-slate-300">
                Search help topics like login, course access, live class, notes,
                test series and payment support.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] border border-white/10 bg-white/10 p-6 backdrop-blur">
              <p className="text-sm font-black uppercase text-cyan-300">
                Quick Contact
              </p>

              <div className="mt-5 space-y-3">
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-3 rounded-2xl bg-white p-4 text-sm font-black text-slate-900"
                >
                  <Phone size={18} />
                  {contactInfo.displayPhone}
                </a>

                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 rounded-2xl bg-white p-4 text-sm font-black text-slate-900"
                >
                  <Mail size={18} />
                  {contactInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2 lg:grid-cols-4">
          {supportCards.map((card) => (
            <article
              key={card.title}
              className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                {card.icon}
              </div>

              <h2 className="text-xl font-black text-[#071f54]">
                {card.title}
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-600">
                {card.description}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">
              Support Process
            </p>

            <h2 className="mt-3 text-3xl font-black text-[#071f54]">
              How to Get Faster Help
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-4">
            {[
              "Identify your issue type",
              "Keep registered details ready",
              "Share screenshot or payment proof",
              "Wait for support verification",
            ].map((step, index) => (
              <article
                key={step}
                className="rounded-[1.5rem] bg-white p-6 ring-1 ring-slate-200"
              >
                <p className="text-4xl font-black text-blue-100">
                  {String(index + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 text-lg font-black text-[#071f54]">
                  {step}
                </h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FaqBlock faqs={faqs} />
      <SupportStrip />
    </main>
  );
};

export const AskDoubt: React.FC = () => {
  const seoTitle =
    "Ask Doubt Online | Maths, Science, JEE, NEET, CBSE Doubt Support - Mediit Academy";
  const seoDescription =
    "Ask doubts online for Maths, Science, Physics, Chemistry, Biology, CBSE, NCERT, JEE Main, NEET UG and board exam preparation at Mediit Academy.";
  const seoKeywords =
    "ask doubt online, student doubt solving, Maths doubt support, Science doubt help, Physics numericals, Chemistry concept doubts, Biology NCERT doubts, JEE doubt solving, NEET doubt support, CBSE board exam doubts";

  usePageSeo({
    path: "/ask-doubt",
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: seoTitle,
          description: seoDescription,
          url: "/ask-doubt",
          keywords: seoKeywords,
        }}
      />

      {/* Different layout - Ask Doubt */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-50 via-white to-blue-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="absolute right-8 top-8 h-52 w-52 rounded-full bg-cyan-200/40 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700">
              <MessageCircleQuestion size={30} />
            </div>

            <p className="mt-6 text-sm font-black uppercase tracking-[0.18em] text-cyan-700">
              Ask Doubt
            </p>

            <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.04em] text-[#071f54] sm:text-5xl lg:text-6xl">
              Ask Maths, Science, JEE, NEET and Board Exam Doubts Online
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600">
              Get support for Maths problems, Physics numericals, Chemistry
              concepts, Biology NCERT topics, CBSE board questions, JEE Main
              practice and NEET UG preparation through mentors and learning
              support.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/login"
                className="inline-flex items-center gap-2 rounded-2xl bg-cyan-500 px-6 py-4 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-cyan-600"
              >
                Login to Ask Doubt
                <ArrowRight size={17} />
              </a>

              <a
                href="/all-classes"
                className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-sm font-black text-slate-900 transition hover:bg-slate-50"
              >
                Explore Subjects
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] bg-white p-6 shadow-2xl ring-1 ring-cyan-100">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-cyan-700">
                Doubt Categories
              </p>

              <div className="mt-5 grid gap-3">
                {[
                  "Maths problem solving",
                  "Physics numericals",
                  "Chemistry concepts",
                  "Biology NCERT topics",
                  "JEE Main questions",
                  "NEET UG revision",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-2xl bg-cyan-50 p-4 text-sm font-bold text-slate-700"
                  >
                    <CheckCircle2 size={18} className="text-cyan-700" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-cyan-700">
              How Doubt Solving Works
            </p>

            <h2 className="mt-3 text-3xl font-black text-[#071f54]">
              Clear Your Concepts Step by Step
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Login",
                desc: "Access your student dashboard with registered details.",
              },
              {
                title: "Choose Subject",
                desc: "Select class, subject, chapter and exam category.",
              },
              {
                title: "Submit Question",
                desc: "Write your doubt clearly and attach image if needed.",
              },
              {
                title: "Review Answer",
                desc: "Understand the explanation and revise the concept.",
              },
            ].map((step, index) => (
              <article
                key={step.title}
                className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <p className="text-4xl font-black text-cyan-100">
                  {String(index + 1).padStart(2, "0")}
                </p>

                <h3 className="mt-4 text-xl font-black text-[#071f54]">
                  {step.title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-600">
                  {step.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SupportStrip />
    </main>
  );
};

const FaqBlock: React.FC<{ faqs: FaqItem[] }> = ({ faqs }) => (
  <section className="px-4 py-12 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-5xl">
      <div className="mb-8 text-center">
        <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">
          FAQs
        </p>

        <h2 className="mt-3 text-3xl font-black text-[#071f54]">
          Common Questions
        </h2>
      </div>

      <div className="space-y-4">
        {faqs.map((faq) => (
          <article
            key={faq.question}
            className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6"
          >
            <h3 className="text-lg font-black text-[#071f54]">
              {faq.question}
            </h3>

            <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
              {faq.answer}
            </p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const FAQs: React.FC = () => {
  const seoTitle =
    "FAQs | Courses, Live Classes, Test Series, Login and Support - Mediit Academy";
  const seoDescription =
    "Mediit Academy FAQs for online courses, live classes, student login, study notes, test series, JEE, NEET, CBSE, payments, refunds and support.";
  const seoKeywords =
    "Mediit Academy FAQs, online course questions, live class FAQs, student login help, JEE course FAQs, NEET course FAQs, CBSE online classes, test series questions, payment support FAQs";

  const faqs: FaqItem[] = [
    {
      question: "How do I join a live class?",
      answer:
        "Login to your student account and open the dashboard or enrolled course section. Live class links are shown according to batch schedule and course activation.",
    },
    {
      question: "Will I get notes and PDFs?",
      answer:
        "Notes, PDFs and practice sheets may be provided according to the selected course, teacher plan and chapter schedule.",
    },
    {
      question: "How do I access test series?",
      answer:
        "Test series access is available from the course dashboard or test section after enrollment and activation.",
    },
    {
      question: "Can I ask doubts online?",
      answer:
        "Yes, students can ask subject-wise doubts if the feature is enabled for their course or dashboard.",
    },
    {
      question: "Can parents track student progress?",
      answer:
        "Progress reports, test results and performance updates may be available depending on dashboard features and course structure.",
    },
  ];

  usePageSeo({
    path: "/faqs",
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }}
      />

      {/* Different layout - FAQs */}
      <section className="bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 text-purple-700">
            <HelpCircle size={30} />
          </div>

          <p className="mt-6 text-sm font-black uppercase tracking-[0.18em] text-purple-700">
            FAQs
          </p>

          <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.04em] text-[#071f54] sm:text-5xl">
            Frequently Asked Questions About Courses, Live Classes and Support
          </h1>

          <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-600">
            Find answers about Mediit Academy courses, student login, live
            classes, study notes, test series, doubt support, payments, refunds
            and mentorship.
          </p>
        </div>
      </section>

      <section className="bg-purple-50 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-4">
          {[
            "Course Enrollment",
            "Live Classes",
            "Notes & PDFs",
            "Test Series",
          ].map((item) => (
            <article
              key={item}
              className="rounded-[1.5rem] bg-white p-6 text-center shadow-sm ring-1 ring-purple-100"
            >
              <h2 className="text-lg font-black text-[#071f54]">{item}</h2>
            </article>
          ))}
        </div>
      </section>

      <FaqBlock faqs={faqs} />
      <SupportStrip />
    </main>
  );
};

export const Career: React.FC = () => {
  const seoTitle =
    "Career at Mediit Academy | Teaching, Mentorship, Content and Support Jobs";
  const seoDescription =
    "Career opportunities at Mediit Academy for teachers, JEE mentors, NEET mentors, content creators, academic counselors, test creators and student support professionals.";
  const seoKeywords =
    "Mediit Academy career, teaching jobs, online teaching jobs, JEE mentor jobs, NEET mentor jobs, academic counselor job, content creator education, test creator jobs, student support career";

  usePageSeo({
    path: "/career",
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: seoTitle,
          description: seoDescription,
          url: "/career",
          keywords: seoKeywords,
        }}
      />

      {/* Different layout - Career */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <Briefcase size={30} />
            </div>

            <p className="mt-6 text-sm font-black uppercase tracking-[0.18em] text-emerald-700">
              Career
            </p>

            <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.04em] text-[#071f54] sm:text-5xl lg:text-6xl">
              Build Your Career with Mediit Academy
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600">
              Join Mediit Academy as a teacher, mentor, academic counselor,
              content creator, test creator or support professional and
              contribute to quality online education.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-6 py-4 text-sm font-black text-white shadow-lg transition hover:bg-emerald-700"
              >
                Contact HR / Support
                <Send size={17} />
              </a>

              <a
                href="/about"
                className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-sm font-black text-slate-900 transition hover:bg-slate-50"
              >
                Explore Academy
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid gap-4">
              {["Teaching Roles", "Content Creation", "Academic Counseling"].map(
                (item) => (
                  <div
                    key={item}
                    className="rounded-[1.5rem] bg-white p-6 shadow-sm ring-1 ring-emerald-100"
                  >
                    <h2 className="text-xl font-black text-[#071f54]">
                      {item}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Work with students, mentors and the academy team to create
                      better learning outcomes.
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          {[
            {
              title: "Teaching Roles",
              desc: "Maths, Science, Physics, Chemistry, Biology, JEE and NEET mentors can contribute through live classes and doubt support.",
            },
            {
              title: "Content & Test Creation",
              desc: "Create NCERT notes, PDF material, practice questions, mock tests and solution explanations.",
            },
            {
              title: "Academic Counseling",
              desc: "Guide students and parents with course selection, study roadmap and exam preparation planning.",
            },
            {
              title: "Student Support",
              desc: "Help students with login, course access, live class updates, payment queries and dashboard support.",
            },
          ].map((item) => (
            <article
              key={item.title}
              className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-2xl font-black text-[#071f54]">
                {item.title}
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                {item.desc}
              </p>
            </article>
          ))}
        </div>
      </section>

      <SupportStrip />
    </main>
  );
};

export const DownloadApp: React.FC = () => {
  const seoTitle = "Download App | Mediit Academy Learning App Coming Soon";
  const seoDescription =
    "Mediit Academy learning app coming soon for live classes, study notes, test series, assignments, student dashboard, doubt support and course updates.";
  const seoKeywords =
    "Mediit Academy app, download learning app, online class app, student learning app, live classes app, test series app, study notes app, doubt solving app, LMS mobile app";

  usePageSeo({
    path: "/download-app",
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
  });

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: seoTitle,
          description: seoDescription,
          url: "/download-app",
          keywords: seoKeywords,
        }}
      />

      {/* Different layout - Download App */}
      <section className="bg-[#07111b] px-4 py-12 text-white sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-rose-200">
              <Download size={30} />
            </div>

            <p className="mt-6 text-sm font-black uppercase tracking-[0.18em] text-rose-200">
              Download App
            </p>

            <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              Mediit Academy Learning App Coming Soon
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300">
              The Mediit Academy mobile app will help students access live
              classes, notes, assignments, test series, doubt support and
              learning updates from a mobile-friendly platform.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/all-classes"
                className="inline-flex items-center gap-2 rounded-2xl bg-rose-300 px-6 py-4 text-sm font-black text-slate-950 transition hover:bg-rose-200"
              >
                Explore Courses
                <ArrowRight size={17} />
              </a>

              <a
                href="/login"
                className="rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-black text-white transition hover:bg-white/15"
              >
                Login / Register
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="mx-auto max-w-sm rounded-[2rem] border border-white/10 bg-white/10 p-6 shadow-2xl">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-[2rem] bg-white text-rose-600">
                <Smartphone size={44} />
              </div>

              <h2 className="mt-6 text-center text-2xl font-black">
                App Launch Soon
              </h2>

              <p className="mt-3 text-center text-sm leading-6 text-slate-300">
                Continue using the website for live classes, notes, tests and
                support until the official app launch.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-4">
          {[
            {
              title: "Live Classes",
              icon: <Video size={24} />,
            },
            {
              title: "Study Notes",
              icon: <BookOpen size={24} />,
            },
            {
              title: "Test Series",
              icon: <ClipboardCheck size={24} />,
            },
            {
              title: "Doubt Support",
              icon: <MessageCircleQuestion size={24} />,
            },
          ].map((item) => (
            <article
              key={item.title}
              className="rounded-[1.5rem] border border-slate-200 bg-white p-6 text-center shadow-sm"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-700">
                {item.icon}
              </div>

              <h2 className="mt-4 text-lg font-black text-[#071f54]">
                {item.title}
              </h2>
            </article>
          ))}
        </div>
      </section>

      <SupportStrip />
    </main>
  );
};

export const RefundPolicy: React.FC = () => (
  <LegalPage
    eyebrow="Refund Policy"
    title="Refund Policy for Online Courses, Test Series and Learning Programs"
    description="Read how refund requests are reviewed for Mediit Academy courses, live batches, test series, mentorship programs and online learning services."
    icon={<FileText size={28} />}
    path="/refund-policy"
    seoTitle="Refund Policy | Online Course Refund Rules - Mediit Academy"
    seoDescription="Mediit Academy refund policy for online courses, live classes, test series, mentorship programs, payment verification, refund eligibility and processing timeline."
    seoKeywords="Mediit Academy refund policy, online course refund, test series refund, live class refund, course cancellation policy, payment verification, refund request, student refund support"
    sections={[
      {
        title: "Refund Request Submission",
        description:
          "To request a refund, the student must contact Mediit Academy support with registered mobile number, registered email, course name, payment date, transaction ID and reason for refund. Incomplete details may delay verification.",
        points: [
          "Registered mobile number or email is required.",
          "Payment proof or transaction reference may be requested.",
          "Refund reason should be clearly mentioned.",
        ],
      },
      {
        title: "Eligibility Review",
        description:
          "Refund eligibility is reviewed based on the course type, batch status, whether learning material has been accessed, live class attendance, test series usage and the time gap between payment and request.",
        points: [
          "Different courses may have different refund conditions.",
          "Used digital content may not be eligible for full refund.",
          "Test series and digital resources may have restricted refund eligibility.",
        ],
      },
      {
        title: "Non-Refundable Cases",
        description:
          "Some cases may not be eligible for refund, especially when the student has accessed significant course content, attended classes, downloaded materials, attempted tests or requested refund after the allowed review period.",
        points: [
          "Completed or heavily used courses may not be refundable.",
          "Downloaded or accessed digital resources can affect eligibility.",
          "Promotional or discounted courses may follow special terms.",
        ],
      },
      {
        title: "Processing Timeline",
        description:
          "If a refund is approved, the processing timeline depends on payment method, bank processing, internal approval and payment gateway rules. Mediit Academy will share updates through official communication channels.",
        points: [
          "Approved refunds may take several working days.",
          "Bank or payment gateway delays are possible.",
          "Refund mode may depend on original or approved payment method.",
        ],
      },
    ]}
    faqs={[
      {
        question: "Is every course refundable?",
        answer:
          "No. Refund eligibility depends on course type, digital access, batch status, usage and request timing.",
      },
      {
        question: "What details are required for refund?",
        answer:
          "Registered mobile number, email, course name, payment date, transaction ID and payment proof may be required.",
      },
    ]}
  />
);

export const PrivacyPolicy: React.FC = () => (
  <LegalPage
    eyebrow="Privacy Policy"
    title="Privacy Policy for Student Data, Learning Activity and Payment Information"
    description="Mediit Academy values student privacy and explains how registration details, contact information, course activity, test progress and payment-related information may be collected and used."
    icon={<Lock size={28} />}
    path="/privacy-policy"
    seoTitle="Privacy Policy | Student Data and Learning Privacy - Mediit Academy"
    seoDescription="Mediit Academy Privacy Policy explains student data collection, course activity, contact information, payment data, communication usage and data protection practices."
    seoKeywords="Mediit Academy privacy policy, student data privacy, online learning privacy, course data protection, payment data privacy, student information security, LMS privacy policy"
    sections={[
      {
        title: "Information We Collect",
        description:
          "Mediit Academy may collect student name, mobile number, email address, class, course preference, enrolled batch details, payment reference, login activity, learning progress, test activity and support communication details.",
        points: [
          "Registration and profile information.",
          "Course enrollment and learning activity.",
          "Payment reference and transaction-related details.",
          "Support messages and communication history.",
        ],
      },
      {
        title: "How We Use Student Data",
        description:
          "Student data is used to create accounts, provide course access, share live class updates, manage enrolled courses, track progress, support test series, provide academic guidance and improve the learning experience.",
        points: [
          "Course access and student dashboard management.",
          "Live class reminders and batch updates.",
          "Test progress, performance report and improvement tracking.",
          "Support, counseling and academic communication.",
        ],
      },
      {
        title: "Payment and Transaction Information",
        description:
          "Payment-related information may be used for enrollment confirmation, invoice support, refund verification and payment issue resolution. Students should share payment proof only through official support channels.",
        points: [
          "Payment verification for enrollment.",
          "Invoice and receipt support.",
          "Refund request review.",
          "Failed transaction support.",
        ],
      },
      {
        title: "Data Protection Practices",
        description:
          "Mediit Academy follows reasonable security practices to protect student information from unauthorized access, misuse or unnecessary exposure. Students should also keep login details confidential and avoid sharing OTPs.",
        points: [
          "Do not share OTP or login credentials.",
          "Use only official Mediit Academy communication channels.",
          "Report suspicious activity to support.",
          "Keep registered contact details updated.",
        ],
      },
      {
        title: "Communication and Updates",
        description:
          "Students may receive academic updates, course reminders, live class alerts, test notifications, support responses and promotional learning information through phone, SMS, email or other communication channels.",
        points: [
          "Batch schedule and live class reminders.",
          "Course updates and test notifications.",
          "Support responses and important account alerts.",
          "Relevant academy announcements.",
        ],
      },
      {
        title: "Policy Updates",
        description:
          "Mediit Academy may update this Privacy Policy when required due to platform improvements, legal requirements, new features or service changes. Students and parents should review this page periodically.",
        points: [
          "Policy may change when new features are added.",
          "Updated policy will apply after publication.",
          "Continued use of the platform means acceptance of updated terms.",
        ],
      },
    ]}
    faqs={[
      {
        question: "Why does Mediit Academy collect student data?",
        answer:
          "Student data is used for registration, course access, dashboard management, live class updates, test progress, support and academic communication.",
      },
      {
        question: "Can the privacy policy change?",
        answer:
          "Yes, the policy may be updated due to platform changes, new features or compliance requirements.",
      },
    ]}
  />
);

export const TermsAndConditions: React.FC = () => (
  <LegalPage
    eyebrow="Terms & Conditions"
    title="Terms and Conditions for Using Mediit Academy Learning Platform"
    description="These terms explain student account responsibility, course access rules, content usage, payment guidelines, platform behavior and service updates for Mediit Academy users."
    icon={<ShieldCheck size={28} />}
    path="/terms-and-conditions"
    seoTitle="Terms and Conditions | Course Access and Platform Rules - Mediit Academy"
    seoDescription="Mediit Academy Terms and Conditions for student accounts, course content, live classes, test series, payments, refunds, platform rules and user responsibilities."
    seoKeywords="Mediit Academy terms and conditions, online course terms, student account rules, live class terms, course content usage, test series rules, payment terms, LMS terms"
    sections={[
      {
        title: "Student Account Responsibility",
        description:
          "Students must provide correct information during registration and keep login credentials safe. Account sharing, unauthorized access, fake information, misuse or suspicious activity may result in temporary or permanent restriction.",
        points: [
          "Do not share password, OTP or login details.",
          "Use correct name, mobile number and email.",
          "Report account issues to official support.",
        ],
      },
      {
        title: "Course Content Usage",
        description:
          "Live classes, recorded videos, PDFs, notes, assignments, test series, practice questions and learning material are provided only for enrolled student learning. Copying, reselling, recording, redistributing or public sharing is not allowed.",
        points: [
          "Content is for personal learning use.",
          "Unauthorized distribution is restricted.",
          "Downloaded resources should not be misused.",
        ],
      },
      {
        title: "Live Classes and Batch Schedule",
        description:
          "Live class schedule may depend on teacher availability, academic calendar, batch plan and technical conditions. Mediit Academy may reschedule, replace or update classes when required for better learning delivery.",
        points: [
          "Students should join classes on time.",
          "Schedule changes may be communicated through official channels.",
          "Class access depends on active enrollment.",
        ],
      },
      {
        title: "Payments, Refunds and Course Access",
        description:
          "Course access is usually activated after successful payment confirmation. Refunds, cancellations and payment disputes are handled according to the Refund Policy, course type and enrollment status.",
        points: [
          "Payment confirmation may be required.",
          "Refund requests follow the Refund Policy.",
          "Promotional courses may have special terms.",
        ],
      },
      {
        title: "Acceptable Platform Use",
        description:
          "Students should use the platform respectfully. Spam, abusive language, cheating, content misuse, unauthorized sharing, harmful activity or interference with platform security is not allowed.",
        points: [
          "Respect teachers, mentors and other students.",
          "Do not post spam or irrelevant content.",
          "Do not attempt unauthorized access.",
        ],
      },
    ]}
    faqs={[
      {
        question: "Can I share my course login with others?",
        answer:
          "No, account sharing is not allowed. Course access is for the enrolled student only.",
      },
      {
        question: "Can Mediit Academy change class timing?",
        answer:
          "Yes, class schedules may be updated depending on batch plan, teacher availability or academic requirements.",
      },
    ]}
  />
);

export default HelpCenter;